import { useEffect, useState } from "react";
import {
  ShieldCheck,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  pending: {
    label: "Pending Review",
    icon: Clock,
    cls: "bg-brand-gold/15 text-brand-gold-dark border-brand-gold/40",
  },
  approved: {
    label: "Approved",
    icon: CheckCircle2,
    cls: "bg-brand-green/15 text-brand-green border-brand-green/40",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    cls: "bg-red-50 text-red-700 border-red-200",
  },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] ${meta.cls}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

const RejectModal = ({ open, onClose, onSubmit, submitting, applicant }) => {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid="tax-exempt-reject-modal"
        className="w-full max-w-md rounded-3xl bg-white p-6 sm:p-7 shadow-2xl"
      >
        <h3 className="font-display font-black text-[20px] text-brand-charcoal tracking-tight">
          Reject tax-exempt request?
        </h3>
        <p className="mt-1.5 text-[12.5px] text-brand-charcoal/65 leading-snug">
          {applicant?.email} · {applicant?.organization || "—"}
        </p>
        <label className="mt-4 block text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">
          Reason (sent to the applicant)
        </label>
        <textarea
          data-testid="tax-exempt-reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="mt-1.5 w-full rounded-2xl border border-black/10 bg-brand-cream/40 px-3.5 py-3 text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
          placeholder="e.g. Certificate is expired — please upload a current copy."
        />
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-black/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-cream"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="tax-exempt-reject-confirm"
            onClick={() => onSubmit(reason.trim())}
            disabled={submitting || reason.trim().length < 3}
            className="rounded-full bg-red-600 text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Rejecting…" : "Reject application"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TaxExemptTab = () => {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // user_id currently being acted on
  const [rejectFor, setRejectFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/admin/tax-exempt", { params });
      setItems(data.items || []);
      setPendingCount(data.pending_count || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const viewDoc = async (userId) => {
    try {
      const resp = await api.get(`/admin/tax-exempt/${userId}/doc`, {
        responseType: "blob",
      });
      // Browsers block window.open() called after an async await because it
      // no longer counts as a user gesture. An anchor click, on the other
      // hand, opens correctly in a new tab from any context.
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open");
    }
  };

  const approve = async (userId, email) => {
    setBusy(userId);
    try {
      const { data } = await api.post(`/admin/tax-exempt/${userId}/approve`);
      toast.success(
        `Approved · ${data.invoices_recomputed} draft invoice${
          data.invoices_recomputed === 1 ? "" : "s"
        } recalculated for ${email}.`
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (reason) => {
    if (!rejectFor) return;
    setBusy(rejectFor.user_id);
    try {
      await api.post(`/admin/tax-exempt/${rejectFor.user_id}/reject`, { reason });
      toast.success(`Rejected · ${rejectFor.email} notified.`);
      setRejectFor(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  const STATUS_FILTERS = [
    { id: "pending", label: `Pending${pendingCount ? ` · ${pendingCount}` : ""}` },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "all", label: "All" },
  ];

  return (
    <div data-testid="admin-tax-exempt-tab" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-black text-[20px] text-brand-charcoal tracking-tight">
            Tax-Exempt Certifications
          </h3>
          <p className="mt-1 text-[12.5px] text-brand-charcoal/60 leading-snug max-w-2xl">
            Review W-9 / ST-119.1 / resale certificates submitted by agency
            users. Approving switches all their draft &amp; future invoices to
            $0 tax; rejecting notifies them with the reason.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="tax-exempt-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-cream"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div
        role="tablist"
        data-testid="tax-exempt-status-filter"
        className="inline-flex items-center gap-1 rounded-full bg-brand-cream/40 border border-black/10 p-1 flex-wrap"
      >
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={statusFilter === f.id}
            onClick={() => setStatusFilter(f.id)}
            data-testid={`tax-exempt-filter-${f.id}`}
            className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] transition-colors ${
              statusFilter === f.id
                ? "bg-brand-charcoal text-white"
                : "text-brand-charcoal/65 hover:text-brand-charcoal"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-[13px] text-brand-charcoal/55">Loading applications…</p>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-3xl border border-black/5 bg-brand-cream/60 p-8 text-center">
          <ShieldCheck className="h-8 w-8 mx-auto text-brand-charcoal/40" />
          <p className="mt-3 font-display font-black text-[16px] text-brand-charcoal">
            No {statusFilter === "all" ? "" : statusFilter} tax-exempt requests
          </p>
          <p className="mt-1.5 text-[12.5px] text-brand-charcoal/55">
            You&apos;ll see requests here when agency users upload their W-9 or
            exemption certificate.
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-2" data-testid="tax-exempt-list">
          {items.map((it) => (
            <li
              key={it.user_id}
              data-testid={`tax-exempt-row-${it.user_id}`}
              className="rounded-2xl bg-white border border-black/5 p-4 sm:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-black text-[18px] sm:text-[19px] text-brand-charcoal tracking-tight leading-tight">
                      {it.name || "—"}
                    </p>
                    <StatusBadge status={it.status} />
                  </div>
                  <p className="mt-0.5 text-[12.5px] text-brand-charcoal/60 truncate">
                    {it.email}
                    {it.organization && <> · <b className="text-brand-charcoal/80">{it.organization}</b></>}
                  </p>
                  <p className="mt-1 text-[11.5px] text-brand-charcoal/55">
                    Submitted {fmtDate(it.submitted_at)}
                    {it.reviewed_at && ` · Reviewed ${fmtDate(it.reviewed_at)} by ${it.reviewed_by || "admin"}`}
                    {it.filename && ` · File: ${it.filename}`}
                  </p>
                  {it.status === "rejected" && it.reject_reason && (
                    <p className="mt-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-700 leading-snug">
                      <b>Rejection reason:</b> {it.reject_reason}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {it.has_document && (
                    <button
                      type="button"
                      onClick={() => viewDoc(it.user_id)}
                      data-testid={`tax-exempt-view-${it.user_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:border-brand-teal hover:text-brand-teal"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      View doc
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  )}
                  {it.status === "pending" && (
                    <>
                      <button
                        type="button"
                        onClick={() => approve(it.user_id, it.email)}
                        disabled={busy === it.user_id}
                        data-testid={`tax-exempt-approve-${it.user_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green text-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-50"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {busy === it.user_id ? "Approving…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejectFor(it)}
                        disabled={busy === it.user_id}
                        data-testid={`tax-exempt-open-reject-${it.user_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 disabled:opacity-50"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        Reject
                      </button>
                    </>
                  )}
                  {it.status === "approved" && (
                    <button
                      type="button"
                      onClick={() => setRejectFor(it)}
                      disabled={busy === it.user_id}
                      data-testid={`tax-exempt-revoke-${it.user_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50"
                      title="Revoke approval and require re-submission"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <RejectModal
        open={!!rejectFor}
        onClose={() => setRejectFor(null)}
        onSubmit={reject}
        submitting={busy === rejectFor?.user_id}
        applicant={rejectFor}
      />
    </div>
  );
};

export default TaxExemptTab;
